import React, { Suspense, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useTexture } from '@react-three/drei';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { VENUE_TYPES } from '../venueTypes';
import { baseGroundY, onGround } from '../city/cityGround';
import { boxes } from '../game/streetCollision';
import { siteKey } from '../services/roomSpace';

// LotBuildings — a building raised on an empty lot (2026-10-31).
//
// Khari, on HypeTunes Headquarters: "an empty lot behind the spawn with a
// generated building". Every venue until now stood on a building the city model
// already had (⚙ → Sites), and VenueFacades only had to dress its door. A LOT
// site (world_objects kind 'site', data.kind 'lot') has nothing on it, so this
// raises one: a body the size of the site, glass up the front with lit floors,
// a parapet, and — when the venue's type says so — its logo lit on the roof.
// The door, the lamp and the name over the door stay VenueFacades', drawn at the
// same door on this building's front face (local +z = out, like every site).
//
// The look is the TYPE's (World venueTypes `lot`: height and colours, a logo),
// else a plain lit block in the venue's accent. It is SOLID on the street: its
// footprint goes into the street's box obstacles while it stands (streetCollision
// `boxes`), so you walk round it and in at its door, never through a wall.
//
// ⚠ THIS WORLD IS NOT IN METRES: a person is ~0.75 units, a storey ~1.6.

const DEFAULT_LOT = { h: 4.8, body: '#24262b', glass: '#1e2a33', trim: null, logo: null };
const STOREY = 1.6;
const GROUND_FLOOR = 2.1;    // the lobby: above the door, lamp and name sign (VenueFacades tops out at ~1.9)

// One lit-window texture for every lot building, made on first use: warm and
// dark panes in a seeded scatter, so a facade reads as floors of rooms.
let windowsTex = null;
function windows() {
  if (windowsTex) return windowsTex;
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  const g = c.getContext('2d');
  g.fillStyle = '#0b1016'; g.fillRect(0, 0, 256, 256);
  let seed = 7;
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let r = 0; r < 8; r++) {
    for (let k = 0; k < 8; k++) {
      const lit = rnd() < 0.55;
      g.fillStyle = lit ? `rgba(255,${200 + Math.floor(rnd() * 30)},${130 + Math.floor(rnd() * 40)},${0.55 + rnd() * 0.4})` : 'rgba(40,60,80,0.5)';
      g.fillRect(k * 32 + 3, r * 32 + 4, 26, 24);
    }
  }
  windowsTex = new THREE.CanvasTexture(c);
  windowsTex.colorSpace = THREE.SRGBColorSpace;
  windowsTex.wrapS = windowsTex.wrapT = THREE.RepeatWrapping;
  return windowsTex;
}

function RoofLogo({ url, w, y, z }) {
  const tex = useTexture(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  const aspect = tex.image?.width && tex.image?.height ? tex.image.width / tex.image.height : 2.85;
  const lw = Math.min(w * 0.8, 5.2); const lh = lw / aspect;
  return (
    <group position={[0, y + lh / 2 + 0.25, z]}>
      {/* The frame it is mounted on, and the posts down to the roof. */}
      <mesh position={[0, 0, -0.06]}>
        <boxGeometry args={[lw + 0.3, lh + 0.24, 0.08]} />
        <meshStandardMaterial color="#111215" roughness={0.6} metalness={0.4} />
      </mesh>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * lw * 0.32, -lh / 2 - 0.15, -0.06]}>
          <boxGeometry args={[0.08, 0.3, 0.08]} />
          <meshStandardMaterial color="#111215" roughness={0.6} metalness={0.4} />
        </mesh>
      ))}
      <mesh position={[0, 0, 0]}>
        <planeGeometry args={[lw, lh]} />
        <meshStandardMaterial map={tex} transparent alphaTest={0.05} emissive="#ffffff" emissiveMap={tex} emissiveIntensity={0.9} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Lot({ slug, venue, ov }) {
  const s = venue.site;
  const spec = { ...DEFAULT_LOT, ...(VENUE_TYPES[venue.type]?.lot || {}) };
  const trim = spec.trim || venue.accent || '#ffb25e';
  const w = Math.max(3, Number(s.w) || 8); const d = Math.max(3, Number(s.d) || 8); const H = Math.max(GROUND_FLOOR + STOREY, spec.h);
  const at = ov && Number.isFinite(ov.x) && Number.isFinite(ov.z) ? { x: ov.x, z: ov.z, rotY: Number(ov.rotY) || 0 } : { x: s.x, z: s.z, rotY: s.rotY || 0 };
  const y0 = baseGroundY(at.x, at.z);

  // Solid while it stands.
  useEffect(() => {
    const b = { cx: at.x, cz: at.z, hx: w / 2, hz: d / 2, ux: Math.cos(at.rotY), uz: -Math.sin(at.rotY), owner: `lot:${slug}` };
    boxes.push(b);
    return () => { const i = boxes.indexOf(b); if (i >= 0) boxes.splice(i, 1); };
  }, [slug, at.x, at.z, at.rotY, w, d]);

  const glass = useMemo(() => {
    const t = windows().clone();
    t.needsUpdate = true;
    t.repeat.set(Math.max(1, Math.round((w - 0.6) / 1.2)) / 8, Math.max(1, Math.round((H - GROUND_FLOOR - 0.3) / STOREY)) / 8);
    return t;
  }, [w, H]);
  useEffect(() => () => glass.dispose(), [glass]);

  const front = d / 2;
  const upperH = H - GROUND_FLOOR - 0.3;
  const floors = Math.max(1, Math.round(upperH / STOREY));
  return (
    <group position={[at.x, y0, at.z]} rotation={[0, at.rotY, 0]}>
      {/* The body. */}
      <mesh position={[0, H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[w, H, d]} />
        <meshStandardMaterial color={spec.body} roughness={0.85} metalness={0.05} />
      </mesh>
      {/* The lobby: dark glass across the ground floor, lit warm from inside, set
          back so the door and its sign (VenueFacades, on the face) stand clear. */}
      <mesh position={[0, GROUND_FLOOR / 2 + 0.02, front + 0.003]}>
        <planeGeometry args={[w - 0.8, GROUND_FLOOR - 0.12]} />
        <meshStandardMaterial color="#141a1f" emissive="#ffcf8a" emissiveIntensity={0.16} roughness={0.15} metalness={0.6} transparent opacity={0.92} />
      </mesh>
      {/* Glass up the front above it, the floors lit. */}
      <mesh position={[0, GROUND_FLOOR + 0.15 + upperH / 2, front + 0.004]}>
        <planeGeometry args={[w - 0.6, upperH]} />
        <meshStandardMaterial color={spec.glass} map={glass} emissive="#ffffff" emissiveMap={glass} emissiveIntensity={0.55} roughness={0.2} metalness={0.5} />
      </mesh>
      {/* Floor bands and mullions. */}
      {Array.from({ length: floors + 1 }).map((_, i) => (
        <mesh key={`f${i}`} position={[0, GROUND_FLOOR + 0.15 + (i * upperH) / floors, front + 0.03]}>
          <boxGeometry args={[w - 0.4, 0.07, 0.06]} />
          <meshStandardMaterial color="#0e0f12" roughness={0.6} metalness={0.5} />
        </mesh>
      ))}
      {Array.from({ length: Math.max(2, Math.round((w - 0.6) / 1.2)) + 1 }).map((_, i, all) => (
        <mesh key={`m${i}`} position={[-(w - 0.6) / 2 + (i * (w - 0.6)) / (all.length - 1), GROUND_FLOOR + 0.15 + upperH / 2, front + 0.035]}>
          <boxGeometry args={[0.05, upperH, 0.05]} />
          <meshStandardMaterial color="#0e0f12" roughness={0.6} metalness={0.5} />
        </mesh>
      ))}
      {/* A lit trim line under the first floor, in the venue's colour (bloom carries it). */}
      <mesh position={[0, GROUND_FLOOR + 0.05, front + 0.06]}>
        <boxGeometry args={[w - 0.2, 0.05, 0.04]} />
        <meshBasicMaterial color={trim} toneMapped={false} />
      </mesh>
      {/* The parapet. */}
      {[[0, front - 0.06, w, 0.12], [0, -front + 0.06, w, 0.12]].map(([x, z, lw, ld], i) => (
        <mesh key={`p${i}`} position={[x, H + 0.18, z]}>
          <boxGeometry args={[lw, 0.36, ld]} />
          <meshStandardMaterial color="#17181c" roughness={0.8} />
        </mesh>
      ))}
      {[-1, 1].map((sx) => (
        <mesh key={`ps${sx}`} position={[sx * (w / 2 - 0.06), H + 0.18, 0]}>
          <boxGeometry args={[0.12, 0.36, d]} />
          <meshStandardMaterial color="#17181c" roughness={0.8} />
        </mesh>
      ))}
      {spec.logo && (
        <Suspense fallback={null}>
          <RoofLogo url={spec.logo} w={w} y={H + 0.36} z={front - 0.5} />
        </Suspense>
      )}
    </group>
  );
}

export default function LotBuildings() {
  const registry = useWorldStore((s) => s.venueRegistry);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  // The ground's real heights land a few seconds after the city does.
  const [, setGround] = useState(0);
  useEffect(() => onGround(() => setGround((n) => n + 1)), []);
  // Not the one you are inside: a sited room stands IN it, and its windows look
  // out through where these walls would be (MeshInterior, CityModel's own rule).
  const inside = useWorldStore((s) => (s.zoom === ZOOM.INTERIOR ? s.interior : null));
  // (Inside a Hype-U classroom, that is the lobby's building: services/roomSpace siteKey.)
  const insideSite = inside ? siteKey(inside) : null;
  const slugs = Object.keys(registry || {}).filter((k) => registry[k]?.site?.kind === 'lot' && ROOMS[k]?.registry && !ROOMS[k].retired && k !== insideSite);
  if (!slugs.length) return null;
  return <group>{slugs.map((k) => <Lot key={k} slug={k} venue={registry[k]} ov={overrides?.[k] || null} />)}</group>;
}
